import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import moment from 'moment';
import { CardBasicService } from '@app/shared/services/wallet/card-basic.service';
import { CustomerContext } from '@app/shared/services/context/customer.context.service';
import { ContractContext } from '@app/shared/services/context/contract.context.service';
import { IContractStatementParams, IContractStatementQuery } from '@app/shared/models/contract.model';
import { AmplitudeService } from '@app/shared/services/amplitude.service';
import { ACTION_EVENT_PROPERTY, ACTION_NAME, AMPLITUDE_EVENT } from '@app/shared/types/enums/amplitude';
import { openFilePreview } from '@app/shared/utils/file-preview';
import { SubscriberComponent } from '@app/shared/modules/subscriber/base/subscriber.component.base';

@Component({
  selector: 'app-contract-statement',
  templateUrl: './contract-statement.component.html',
  styleUrls: ['./contract-statement.component.sass'],
})
export class ContractStatementComponent extends SubscriberComponent implements OnInit {
  form!: FormGroup;
  isLoading = false;

  maxDate: Date | null = null;
  minDate: Date | null = null;
  dateWithStatement: string[] = [];
  rppUserId!: string;
  contractCode!: string;
  hasNotStatement = false;

  constructor(
    private readonly _cardBasicSvc: CardBasicService,
    private readonly _customerCtxt: CustomerContext,
    private readonly _contractCtxt: ContractContext,
    private readonly _amplitudeSvc: AmplitudeService
  ) {
    super();
    this.rppUserId = this._customerCtxt.getRppUserId();
    this.contractCode = this._contractCtxt.getCurrentContract()?.code || '';
  }

  ngOnInit(): void {
    this.initForm();
    this.setStatementInfo();
    this.logAmplitudeEvent();
  }

  get dateField() {
    return this.form.controls['date'];
  }

  private initForm() {
    this.form = new FormGroup({
      date: new FormControl('', [Validators.required]),
    });

    this._subscriptions.sink = this.dateField.valueChanges.subscribe(value => {
      if (this.dateWithStatement.length > 0) {
        const val = moment(value).startOf('month').format('YYYY-MM-DD');
        const hasStatement = this.dateWithStatement.some(date => date === val);
        this.hasNotStatement = !hasStatement;
      }
    });
  }

  private logAmplitudeEvent() {
    this._amplitudeSvc.logEvent<ACTION_EVENT_PROPERTY>(AMPLITUDE_EVENT.ACTION_INIT, {
      actionName: ACTION_NAME.CONTRACT_STATEMENT,
    });
  }

  private setStatementInfo() {
    const params: IContractStatementParams = {
      rppUserId: this.rppUserId,
      contractCode: this.contractCode,
    };

    this._subscriptions.sink = this._cardBasicSvc.getStatementInfoV2(params).subscribe({
      next: data => {
        if (data && data.length > 0) {
          //data return array of date string order by desc
          const minDate = moment(data[data.length - 1]).endOf('month');
          this.minDate = minDate.toDate();
          const endDate = moment(data[0]).endOf('month');
          this.maxDate = endDate.toDate();
          this.dateField.setValue(endDate);
          this.dateWithStatement = data;
        } else {
          this.dateField.disable();
          this.hasNotStatement = true;
        }
      },
      error: err => {
        this.dateField.disable();
      },
    });
  }

  showPreviewOnline() {
    this.isLoading = true;
    this._subscriptions.sink = this.getContractStatement().subscribe(res => {
      openFilePreview(res?.file || '');
      this.isLoading = false;
    });
  }

  private getContractStatement() {
    const { year, month } = this.getYearAndMonth();
    const params: IContractStatementQuery = {
      rppUserId: this.rppUserId,
      contractCode: this.contractCode,
      month,
      year,
    };

    return this._cardBasicSvc.getStatementPdfV2(params);
  }

  getYearAndMonth() {
    const date = this.dateField.value;

    return {
      year: date.year(),
      month: date.month() + 1,
    };
  }
}
